import { useState } from "react";
import { motion } from "framer-motion";
import { CreditCard, Download, Share2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { TiltCard } from "./TiltCard";
import { useLang } from "../store/lang";
import { VENUE_NAME, CEREMONY_DATE_LABEL } from "../config";

const drawCard = async (guest, status = false) => {
  await document.fonts.ready;
  const W = 1080;
  const H = status ? 1920 : 1350;
  const oy = status ? 300 : 0;
  const cv = document.createElement("canvas");
  cv.width = W;
  cv.height = H;
  const c = cv.getContext("2d");

  const bg = c.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#070814");
  bg.addColorStop(0.55, "#0B0D1B");
  bg.addColorStop(1, "#19102E");
  c.fillStyle = bg;
  c.fillRect(0, 0, W, H);

  for (let i = 0; i < (status ? 260 : 170); i++) {
    c.globalAlpha = 0.25 + Math.random() * 0.75;
    c.fillStyle = "#F3E5AB";
    c.beginPath();
    c.arc(Math.random() * W, Math.random() * H, Math.random() * 1.8 + 0.4, 0, Math.PI * 2);
    c.fill();
  }
  c.globalAlpha = 1;

  const moonY = 210 + (status ? 120 : 0);
  const mg = c.createRadialGradient(W - 220, moonY, 10, W - 220, moonY, 140);
  mg.addColorStop(0, "#FFF7DD");
  mg.addColorStop(0.5, "#F3E5AB");
  mg.addColorStop(1, "rgba(212,175,55,0)");
  c.fillStyle = mg;
  c.beginPath();
  c.arc(W - 220, moonY, 140, 0, Math.PI * 2);
  c.fill();
  c.fillStyle = "#F6ECC2";
  c.beginPath();
  c.arc(W - 220, moonY, 72, 0, Math.PI * 2);
  c.fill();

  c.strokeStyle = "#D4AF37";
  c.lineWidth = 5;
  c.strokeRect(44, 44, W - 88, H - 88);
  c.lineWidth = 1.6;
  c.strokeRect(64, 64, W - 128, H - 128);

  const cx = W / 2;
  c.textAlign = "center";

  if (status) {
    c.fillStyle = "#F4D3D5";
    c.font = "500 30px Outfit";
    c.fillText("✦  Y O U   A R E   I N V I T E D  ✦", cx, 190);
  }

  c.fillStyle = "#D4AF37";
  c.font = "600 36px Cinzel";
  c.fillText("✦   A Q I Q A H   ✦", cx, 235 + oy);
  c.font = "400 44px serif";
  c.fillStyle = "#F4D3D5";
  c.fillText("عقيقة", cx, 300 + oy);

  c.fillStyle = "#F3E5AB";
  c.font = "700 92px Cinzel";
  c.fillText("Haya Noor Jamal", cx, 435 + oy);

  c.font = "400 52px 'Marck Script'";
  c.fillStyle = "#F4D3D5";
  c.fillText("with love to", cx, 575 + oy);

  c.font = "400 96px 'Marck Script'";
  c.fillText(guest.slice(0, 26), cx, 705 + oy);

  c.strokeStyle = "rgba(212,175,55,0.7)";
  c.lineWidth = 1.5;
  c.beginPath();
  c.moveTo(cx - 260, 775 + oy);
  c.lineTo(cx + 260, 775 + oy);
  c.stroke();

  c.font = "300 32px Outfit";
  c.fillStyle = "#E2E8F0";
  c.fillText("requests the honour of your presence at the", cx, 848 + oy);
  c.font = "600 40px Cinzel";
  c.fillStyle = "#F3E5AB";
  c.fillText("Aqiqah Ceremony of Haya Noor Jamal", cx, 912 + oy);

  c.font = "600 44px Cinzel";
  c.fillStyle = "#D4AF37";
  c.fillText(`${CEREMONY_DATE_LABEL}  ·  7 PM onwards`, cx, 1005 + oy);

  c.font = "500 34px Outfit";
  c.fillStyle = "#E2E8F0";
  c.fillText(VENUE_NAME, cx, 1072 + oy);
  c.font = "300 24px Outfit";
  c.fillStyle = "#94A3B8";
  c.fillText("Near Kalindi Kunj Metro Station, New Delhi", cx, 1116 + oy);
  c.fillText("Dress code: Royal Stardust & Fairytale Formal", cx, 1158 + oy);

  if (status) {
    c.font = "500 28px Outfit";
    c.fillStyle = "#D4AF37";
    c.fillText(`✦  ${window.location.host}  ✦`, cx, H - 190);
  }

  c.fillStyle = "#150F2E";
  c.fillRect(64, H - 132, W - 128, 68);
  const towers = [
    [190, 46],
    [330, 76],
    [500, 108],
    [610, 108],
    [780, 76],
    [910, 46],
  ];
  for (const [x, h] of towers) {
    c.fillStyle = "#150F2E";
    c.fillRect(x - 26, H - 132 - h, 52, h);
    c.fillStyle = "#D4AF37";
    c.beginPath();
    c.moveTo(x - 33, H - 132 - h);
    c.lineTo(x, H - 132 - h - 38);
    c.lineTo(x + 33, H - 132 - h);
    c.closePath();
    c.fill();
    c.fillStyle = "#F3E5AB";
    c.beginPath();
    c.arc(x, H - 132 - h - 42, 4, 0, Math.PI * 2);
    c.fill();
  }

  return cv;
};

export const InviteCard = () => {
  const { ur } = useLang();
  const [name, setName] = useState("");
  const [status, setStatus] = useState(false);
  const [busy, setBusy] = useState(false);
  const display = name.trim() || "Dearest Guest";
  const slug = display.replace(/\s+/g, "-").toLowerCase();
  const fileName = status ? `aqiqah-status-${slug}.png` : `aqiqah-invite-${slug}.png`;

  const makeBlob = async () => {
    const cv = await drawCard(display, status);
    return new Promise((r) => cv.toBlob(r, "image/png"));
  };

  const download = async () => {
    setBusy(true);
    try {
      const blob = await makeBlob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(
        status
          ? ur ? "اسٹیٹس کارڈ ڈاؤن لوڈ ہو گیا" : "Your WhatsApp status card is downloaded"
          : ur ? "دعوت نامہ ڈاؤن لوڈ ہو گیا" : "Your invitation card is downloaded"
      );
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    setBusy(true);
    try {
      const blob = await makeBlob();
      const file = new File([blob], fileName, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Aqiqah of Haya Noor Jamal",
          text: `You are warmly invited to the Aqiqah of Haya Noor Jamal — ${CEREMONY_DATE_LABEL} at ${VENUE_NAME}`,
        });
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(a.href);
        window.open(
          `https://wa.me/?text=${encodeURIComponent(
            `You are warmly invited to the Aqiqah of Haya Noor Jamal — ${CEREMONY_DATE_LABEL} at ${VENUE_NAME}. (My invitation card image is downloaded — attaching it here!)`
          )}`,
          "_blank"
        );
        toast.success(
          status
            ? ur ? "کارڈ ڈاؤن لوڈ ہو گیا — واٹس ایپ اسٹیٹس میں لگائیں" : "Card downloaded — post it to your WhatsApp status"
            : ur ? "کارڈ ڈاؤن لوڈ ہو گیا — واٹس ایپ چیٹ میں لگائیں" : "Card downloaded — attach it in the WhatsApp chat that just opened"
        );
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(ur ? "کارڈ نہیں بن سکا — دوبارہ کوشش کریں" : "Could not create the card — try again");
    } finally {
      setBusy(false);
    }
  };

  const formats = [
    { id: "invite-format-card", value: false, icon: CreditCard, en: "Invitation Card", ur: "دعوت نامہ" },
    { id: "invite-format-status", value: true, icon: Smartphone, en: "WhatsApp Status", ur: "واٹس ایپ اسٹیٹس" },
  ];

  return (
    <section id="invite-card" className="relative py-28 sm:py-36 px-6" data-testid="invite-card-section">
      <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="order-2 lg:order-1"
        >
          <p className="eyebrow mb-4">{ur ? "ہر مہمان کے لیے تحفہ" : "A gift for every guest"}</p>
          <h2 className="font-display text-3xl sm:text-5xl text-amber-100 font-light">
            {ur ? "آپ کا ذاتی دعوت نامہ" : "Your personal invitation card"}
          </h2>
          <p className="mt-5 text-slate-300/90 font-light leading-relaxed max-w-md">
            {ur
              ? "اپنا نام لکھیں اور شاہی کارڈ خود کو آپ کے نام لکھتا دیکھیں۔ دعوت نامہ ڈاؤن لوڈ کریں یا عمودی واٹس ایپ اسٹیٹس کارڈ ایک ٹیپ میں شیئر کریں۔"
              : "Type your name and watch the royal card write itself to you. Download the invitation, or switch to the vertical WhatsApp Status card and post it in one tap."}
          </p>

          <div className="mt-8 flex flex-col gap-4 max-w-md">
            <input
              data-testid="invite-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={26}
              placeholder={ur ? "اپنا نام لکھیں…" : "Type your name…"}
              className="bg-white/5 border border-amber-200/25 rounded-full px-6 py-4 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-amber-300/60 transition-colors duration-300"
            />
            <div data-testid="invite-format-switch" className="inline-flex self-start rounded-full border border-amber-200/25 bg-white/5 p-1">
              {formats.map((f) => {
                const active = status === f.value;
                return (
                  <button
                    key={f.id}
                    data-testid={f.id}
                    aria-pressed={active}
                    onClick={() => setStatus(f.value)}
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-[0.7rem] tracking-[0.15em] uppercase transition-colors duration-300 ${
                      active ? "bg-gradient-to-r from-[#f3e5ab] to-[#d4af37] text-[#1b1407] font-semibold" : "text-amber-100/80 hover:text-amber-100"
                    }`}
                  >
                    <f.icon size={13} /> {ur ? f.ur : f.en}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-3">
              <button data-testid="invite-download-btn" onClick={download} disabled={busy} className="btn-gold disabled:opacity-60">
                <Download size={15} /> {busy ? (ur ? "تیار ہو رہا ہے…" : "Crafting…") : ur ? "کارڈ ڈاؤن لوڈ کریں" : "Download Card"}
              </button>
              <button data-testid="invite-share-btn" onClick={share} disabled={busy} className="btn-ghost disabled:opacity-60">
                <Share2 size={15} /> {status ? (ur ? "اسٹیٹس پر لگائیں" : "Post to Status") : ur ? "واٹس ایپ پر شیئر کریں" : "Share on WhatsApp"}
              </button>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30, rotateX: 14 }}
          whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="order-1 lg:order-2"
        >
          <TiltCard
            max={8}
            className={`relative mx-auto rounded-[1.6rem] border-2 border-amber-300/50 bg-gradient-to-b from-[#0B0D1B] to-[#19102E] shadow-[0_0_60px_rgba(212,175,55,0.18)] overflow-hidden transition-[max-width] duration-500 ${
              status ? "max-w-[17rem] aspect-[9/16]" : "max-w-sm aspect-[4/5]"
            }`}
            data-testid="invite-card-preview"
            data-format={status ? "status" : "card"}
          >
            <div className="absolute inset-3 rounded-[1.1rem] border border-amber-200/30 pointer-events-none" />
            <div className="absolute top-6 right-6 w-14 h-14 rounded-full bg-[radial-gradient(circle_at_35%_35%,#fffbe8,#f3e5ab_55%,#c9a84c)] shadow-[0_0_30px_8px_rgba(243,229,171,0.35)]" />
            <div className="relative h-full flex flex-col items-center justify-center text-center px-8">
              {status && <p className="text-[0.6rem] tracking-[0.35em] uppercase text-rose-200/80 mb-5">✦ you are invited ✦</p>}
              <p className="font-royal text-[0.7rem] tracking-[0.5em] text-amber-300">✦ AQIQAH ✦</p>
              <p className="mt-1 text-rose-200/90 text-lg" lang="ar">عقيقة</p>
              <h3 className="font-royal text-2xl sm:text-3xl gold-text mt-5 leading-snug">Haya Noor Jamal</h3>
              <p className="font-script text-xl text-rose-200/90 mt-6">with love to</p>
              <p data-testid="invite-card-guest-name" className="font-script text-4xl text-rose-200 mt-1 break-words max-w-full">
                {display}
              </p>
              <div className="my-5 h-px w-32 bg-gradient-to-r from-transparent via-amber-300/70 to-transparent" />
              <p className="text-[0.72rem] text-slate-300 font-light">requests the honour of your presence</p>
              <p className="font-royal text-sm text-amber-100 mt-4 tracking-wider">{CEREMONY_DATE_LABEL} · 7 PM</p>
              <p className="text-xs text-slate-400 mt-2">{VENUE_NAME}, New Delhi</p>
              <p className="text-[0.65rem] text-slate-500 mt-4 tracking-[0.2em] uppercase">Royal Stardust & Fairytale Formal</p>
            </div>
          </TiltCard>
        </motion.div>
      </div>
    </section>
  );
};
